import { WorkflowLookup, WorkflowLookupField, WorkflowLookupItem } from 'models/WorkflowLookup';
import { WorkflowLookupItemInput } from 'services/api/admin/workflowLookups';

// A short name is permanent once saved, so it is derived from the display name only while an entry
// is new. Matches the API's rule: lowercase words joined by underscores.
export function slugify(displayName: string) {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function fieldValue(item: WorkflowLookupItem, field: WorkflowLookupField) {
  return item[field.name];
}

// Whether the live text differs from what the code declares, computed from what is in the boxes
// rather than from the `customized` flag the API sent. The flag describes the last saved state, so
// trusting it leaves the marker up after Restore puts the code's wording back, and misses an edit
// until it is saved. Mirrors WorkflowLookup::SourceText.customized? on the API side.
export function isCustomized(item: WorkflowLookupItem) {
  if (!item.sourceDisplayName) return false;

  return item.displayName !== item.sourceDisplayName || (item.description || null) !== (item.sourceDescription || null);
}

// What a required field being blank looks like, so the form can say so before the API does. The
// API re-checks all of it — this only spares the round trip.
export function missingRequiredFields(item: WorkflowLookupItem, fields: WorkflowLookupField[]) {
  return fields.filter((field) => field.required && !String(fieldValue(item, field) ?? '').trim());
}

// The short name is deliberately not listed: it is derived from the name, so naming it asks for a
// field nobody types into.
export function itemErrors(item: WorkflowLookupItem, fields: WorkflowLookupField[]) {
  const errors: string[] = [];
  if (!item.displayName.trim()) errors.push('a name');
  missingRequiredFields(item, fields).forEach((field) => errors.push(`a ${field.label.toLowerCase()}`));

  return errors;
}

export function sentence(parts: string[]) {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return parts.join(' and ');

  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

// The payload the API takes: the submitted list IS the list, in this order. Derived and
// publish-owned fields are left off — the API drops them anyway, and sending them back would
// suggest they were ours to set.
export function toInput(items: WorkflowLookupItem[], fields: WorkflowLookupField[]): WorkflowLookupItemInput[] {
  return items.map((item) => {
    const input: WorkflowLookupItemInput = {
      apiName: item.apiName.trim(),
      displayName: item.displayName.trim(),
      archived: item.archived,
    };
    if (item.description !== null && item.description !== undefined) input.description = item.description;
    fields.forEach((field) => {
      input[field.name] = fieldValue(item, field);
    });

    return input;
  });
}

let keySeed = 0;

// A stable client-side identity. Keying a row on its short name remounts the input the moment that
// name is derived from the first character typed, which drops focus and swallows the rest.
export function withKeys(items: WorkflowLookupItem[]): WorkflowLookupItem[] {
  return items.map((item) => (item._key ? item : { ...item, _key: `item-${(keySeed += 1)}` }));
}

export function blankItem(fields: WorkflowLookupField[]): WorkflowLookupItem {
  const item: WorkflowLookupItem = {
    _key: `item-${(keySeed += 1)}`,
    _isNew: true,
    apiName: '',
    displayName: '',
    description: null,
    archived: false,
    customized: false,
    sourceDisplayName: null,
    sourceDescription: null,
  };
  fields.forEach((field) => {
    item[field.name] = field.type === 'boolean' ? false : '';
  });

  return item;
}

// A lookup field's options come from the parent list's OWN row for the same scope, which is why the
// editor loads it rather than reading anything embedded here: a client editing its own reasons must
// choose from its own categories, not the global ones.
export function parentKeysFor(lookup: WorkflowLookup) {
  return lookup.fields.filter((field) => field.type === 'lookup' && field.parentKey).map((field) => field.parentKey!);
}
